import React, { useEffect } from "react";
import { Col, Form, FormGroup, Label, Input, FormFeedback } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import ToastComponent from "@/components/shared/Toast";
import useAppDispatch from "@/app/hooks/useAppDispatch";
import { useSelector } from "react-redux";
import { hideToast } from "@/store/contactPremiumSlice";
import { fetchTags } from "@/store/tagsSlice";
import { RootState } from "@/store";

interface ContactFormProps {
  formData: any;
  errors: any;
  touched: any;
  toast: any;
  toastLoading: boolean;
  isFormValid: () => boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (fieldName: string) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  handleClearAll: () => void;
}

const ContactForm: React.FC<ContactFormProps> = ({
  formData,
  errors,
  touched,
  toast,
  toastLoading,
  isFormValid,
  handleChange,
  handleBlur,
  handleSubmit,
  handleClearAll,
}) => {
  const dispatch = useAppDispatch();
  const { tags } = useSelector((state: RootState) => state.tags);

  useEffect(() => {
    dispatch(fetchTags());
  }, [dispatch]);

  return (
    <Col
      md="6"
      className="rounded-md border border-gray-300 text-[16px] p-4 relative"
    >
      {/* Position the toast at the top of the form, inside the border */}
      {toast.visible && (
        <div className="w-full mb-4">
          <ToastComponent
            message={toast.message}
            variant={toast.variant}
            visible={toast.visible}
            duration={3000}
            onClose={() => dispatch(hideToast())}
            position="relative"
            fullWidth={true}
          />
        </div>
      )}

      <p className="text-red-700">* indicates required fields</p>
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label for="firstName">
            <span className="text-red-700">*</span>First Name
          </Label>
          <Input
            type="text"
            name="fromNameFirst"
            id="firstName"
            value={formData.fromNameFirst}
            onChange={handleChange}
            onBlur={() => handleBlur("fromNameFirst")}
            invalid={touched.fromNameFirst && !!errors.fromNameFirst}
          />
          {touched.fromNameFirst && errors.fromNameFirst && (
            <FormFeedback className="!text-red-700">
              {errors.fromNameFirst}
            </FormFeedback>
          )}
        </FormGroup>
        <FormGroup>
          <Label for="lastName">
            <span className="text-red-700">*</span>Last Name
          </Label>
          <Input
            type="text"
            name="fromNameLast"
            id="lastName"
            value={formData.fromNameLast}
            onChange={handleChange}
            onBlur={() => handleBlur("fromNameLast")}
            invalid={touched.fromNameLast && !!errors.fromNameLast}
          />
          {touched.fromNameLast && errors.fromNameLast && (
            <FormFeedback className="!text-red-700">
              {errors.fromNameLast}
            </FormFeedback>
          )}
        </FormGroup>
        <FormGroup>
          <Label for="email">
            <span className="text-red-700">*</span>Email
          </Label>
          <Input
            type="email"
            name="fromEmail"
            id="email"
            value={formData.fromEmail}
            onChange={handleChange}
            onBlur={() => handleBlur("fromEmail")}
            invalid={touched.fromEmail && !!errors.fromEmail}
          />
          {touched.fromEmail && errors.fromEmail && (
            <FormFeedback className="!text-red-700">
              {errors.fromEmail}
            </FormFeedback>
          )}
        </FormGroup>
        <FormGroup>
          <Label for="confirmEmail">
            <span className="text-red-700">*</span>Confirm Email
          </Label>
          <Input
            type="email"
            name="confirmEmail"
            id="confirmEmail"
            value={formData.confirmEmail}
            onChange={handleChange}
            onBlur={() => handleBlur("confirmEmail")}
            invalid={touched.confirmEmail && !!errors.confirmEmail}
          />
          {touched.confirmEmail && errors.confirmEmail && (
            <FormFeedback className="!text-red-700">
              {errors.confirmEmail}
            </FormFeedback>
          )}
        </FormGroup>
        <FormGroup>
          <Label for="subject">
            <span className="text-red-700">*</span>Subject
          </Label>
          <Input
            type="select"
            name="subject"
            id="subject"
            value={formData.subject}
            onChange={(e) => {
              const selectedTag = tags.find(tag => tag.name === e.target.value);
              // Create a synthetic event for the subject
              const subjectEvent = {
                ...e,
                target: { ...e.target, name: 'subject', value: e.target.value }
              };
              handleChange(subjectEvent as React.ChangeEvent<HTMLInputElement>);

              // Create a synthetic event for the tagID
              const tagEvent = {
                ...e,
                target: { ...e.target, name: 'tagID', value: selectedTag ? String(selectedTag.id) : '' }
              };
              handleChange(tagEvent as React.ChangeEvent<HTMLInputElement>);
            }}
            onBlur={() => handleBlur("subject")}
            invalid={touched.subject && !!errors.subject}
          >
            <option value="" disabled hidden>Select a subject</option>
            {tags.map((opt: { id: number; name: string }) => (
              <option key={opt.id} value={opt.name}>{opt.name}</option>
            ))}
          </Input>
          {touched.subject && errors.subject && (
            <FormFeedback className="!text-red-700">
              {errors.subject}
            </FormFeedback>
          )}
        </FormGroup>
        <FormGroup>
          <Label for="message">
            <span className="text-red-700">*</span>Message
          </Label>
          <Input
            type="textarea"
            name="message"
            id="message"
            value={formData.message}
            onChange={handleChange}
            onBlur={() => handleBlur("message")}
            invalid={touched.message && !!errors.message}
          />
          {touched.message && errors.message && (
            <FormFeedback className="!text-red-700">
              {errors.message}
            </FormFeedback>
          )}
        </FormGroup>
        <button
          className="!text-[14px] !text-[#003C71] !bg-white !border-none !px-4 !py-2 !rounded-full hover:!bg-[#e6e6e6] transition-colors duration-200"
          onClick={handleClearAll}
          type="button"
        >
          CLEAR ALL{" "}
          <FontAwesomeIcon
            className="text-[12px] !ml-2"
            icon={faChevronRight}
          />
        </button>
        <button
          className={`float-right !text-[14px] !border-none !rounded-full !px-4 !py-2 ${isFormValid()
            ? "!bg-[#003C71] !text-white hover:!bg-[#0057A4]"
            : "!bg-[#e0e0e0] !text-[#8c9599]"
            }`}
          type="submit"
          disabled={!isFormValid() || toastLoading}
          style={{
            cursor:
              isFormValid() && !toastLoading ? "pointer" : "not-allowed",
          }}
        >
          {toastLoading ? "SENDING..." : "SUBMIT"}{" "}
          <FontAwesomeIcon
            className="text-[12px] ml-2"
            icon={faChevronRight}
          />
        </button>
      </Form>
    </Col>
  );
};

export default ContactForm;
